import { useState } from "react";
import { CheckCircle2, Loader2, Mail, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const TONE = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  accepted: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  declined: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

/** The queue of public hall-hire requests, and the two decisions staff can take on each. */
export const HireRequests = ({ requests, rooms, canWrite, onChanged }) => {
  const [open, setOpen] = useState(null);
  const [mode, setMode] = useState("");
  const [room, setRoom] = useState("");
  const [charge, setCharge] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const start = (request, next) => {
    setOpen(request);
    setMode(next);
    setRoom(request.space_code || "");
    setCharge("");
    setReason("");
  };

  const decide = async () => {
    setBusy(true);
    try {
      const body = mode === "approve"
        ? { space_code: room, override_charge: charge === "" ? null : Number(charge) }
        : { reason };
      const res = await api.post(`/hall-hire/requests/${open.id}/${mode}`, body);
      toast.success(mode === "approve"
        ? `Booked as ${res.data.booking.booking_ref}`
        : "Request declined");
      if (!res.data.email_sent && res.data.mailto) {
        toast.message("Email is not connected yet — a draft is ready", {
          action: { label: "Open draft", onClick: () => window.open(res.data.mailto) },
        });
      }
      setOpen(null);
      await onChanged();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const pending = requests.items.filter((r) => r.status === "pending");

  return (
    <div className="space-y-4" data-testid="hire-requests">
      <Panel testId="panel-hire-requests" kicker="From your website"
             title="Hall hire requests"
             actions={(
               <div className="flex gap-1.5">
                 <Chip className={TONE.pending}>{requests.counters.pending} awaiting you</Chip>
                 <Chip className={TONE.accepted}>{requests.counters.accepted} booked</Chip>
                 <Chip className={TONE.declined}>{requests.counters.declined} declined</Chip>
               </div>
             )}>
        <Table testId="hire-requests-table"
               head={<><Th>Reference</Th><Th>Who</Th><Th>Space asked for</Th><Th>When</Th>
                 <Th>People</Th><Th>Status</Th><Th /></>}>
          {requests.items.length === 0 && (
            <Empty colSpan={7} label="No requests yet. Switch the public form on in Hire settings." />
          )}
          {requests.items.map((r) => (
            <tr key={r.request_ref} data-testid={`hire-request-row-${r.request_ref}`}>
              <Td className="font-mono text-[11px] text-primary">{r.request_ref}</Td>
              <Td>
                <div className="text-[12px] font-medium text-foreground">
                  {r.organisation || r.requester_name}
                </div>
                <div className="font-mono text-[10.5px] text-muted-foreground">{r.email}</div>
                <div className="text-[11px] text-muted-foreground">{r.purpose}</div>
              </Td>
              <Td className="text-[11.5px]">{r.space_name || "Any suitable space"}</Td>
              <Td className="font-mono text-[11px]">
                {r.starts_at?.slice(0, 16).replace("T", " ")} – {r.ends_at?.slice(11, 16)}
              </Td>
              <Td className="font-mono text-[11px]">{r.attendees || "—"}</Td>
              <Td>
                <Chip className={TONE[r.status]}>{r.status}</Chip>
                {r.booking_ref && (
                  <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                    {r.booking_ref}
                  </div>
                )}
                {r.decline_reason && (
                  <div className="mt-1 text-[10.5px] text-muted-foreground">{r.decline_reason}</div>
                )}
              </Td>
              <Td>
                {canWrite && r.status === "pending" && (
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" data-testid={`hire-approve-${r.request_ref}`}
                            onClick={() => start(r, "approve")}
                            className="h-7 rounded-full bg-primary px-2.5 text-[10px] text-primary-foreground hover:bg-primary/90">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`hire-decline-${r.request_ref}`}
                            onClick={() => start(r, "decline")}
                            className="h-7 rounded-full border-border px-2.5 text-[10px] text-red-600 dark:text-red-400">
                      <XCircle className="mr-1 h-3 w-3" /> Decline
                    </Button>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
        {pending.length > 0 && (
          <div className="flex items-center gap-2 border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
            <Mail className="h-3.5 w-3.5 text-primary" />
            Accepting a request creates a confirmed booking with the rate card applied, and refuses
            it if the space is already taken. The requester is emailed either way.
          </div>
        )}
      </Panel>

      <Dialog open={Boolean(open)} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-md border-border bg-card" data-testid="hire-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {mode === "approve" ? "Accept and book" : "Decline this request"}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {open?.requester_name} · {open?.starts_at?.slice(0, 16).replace("T", " ")} –{" "}
              {open?.ends_at?.slice(11, 16)} · {open?.attendees || 0} people
            </DialogDescription>
          </DialogHeader>

          {mode === "approve" ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] text-muted-foreground">Book into</Label>
                <Select value={room} onValueChange={setRoom}>
                  <SelectTrigger data-testid="hire-decision-room" className="bg-muted/40">
                    <SelectValue placeholder="Choose the space" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {s.name}{s.room_number ? ` (${s.room_number})` : ""} · seats {s.capacity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] text-muted-foreground">
                  Charge (leave blank to use the rate card)
                </Label>
                <Input type="number" value={charge} data-testid="hire-decision-charge"
                       onChange={(e) => setCharge(e.target.value)}
                       className="bg-muted/40 font-mono" />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Textarea rows={3} value={reason} data-testid="hire-decision-reason"
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="What should we tell them? (at least 5 characters)"
                        className="bg-muted/40 text-[12.5px]" />
              {reason.trim().length > 0 && reason.trim().length < 5 && (
                <p className="text-[11px] text-red-600 dark:text-red-400"
                   data-testid="hire-decision-reason-error">
                  Give at least 5 characters — this is sent to the requester.
                </p>
              )}
            </div>
          )}

          <Button onClick={decide} data-testid="hire-decision-confirm"
                  disabled={busy || (mode === "approve" ? !room : reason.trim().length < 5)}
                  className={`h-9 rounded-full ${mode === "approve"
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-red-600 text-white hover:bg-red-600/90"}`}>
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {mode === "approve" ? "Accept and create the booking" : "Decline and notify"}
          </Button>
          {open?.notes && (
            <p className="text-[11px] text-muted-foreground">
              Their note: {open.notes} · received {dateTime(open.created_at)}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
